'use client';

import 'prosemirror-view/style/prosemirror.css';

import { useMemo, useState } from 'react';
import { Doc } from '@ametista-ai/editor/extensions/Doc';
import { EmojiMenuTrigger } from '@ametista-ai/editor/extensions/EmojiMenuTrigger';
import { HardBreak } from '@ametista-ai/editor/extensions/HardBreak';
import { Paragraph } from '@ametista-ai/editor/extensions/Paragraph';
import { Placeholder } from '@ametista-ai/editor/extensions/Placeholder';
import { Text } from '@ametista-ai/editor/extensions/Text';
import { EmojiMenuContext, EmojiMenuStore } from '@ametista-ai/editor/plugins/EmojiPlugin/store';
import { createSchema } from '@ametista-ai/editor/schema';
import {
  getCommandsFromExtensions,
  getInputRulesFromExtensions,
  getPluginsFromExtensions,
  parseContentToDocSafe,
} from '@ametista-ai/editor/utils';
import { ProseMirror, ProseMirrorDoc, reactKeys } from '@handlewithcare/react-prosemirror';
import { baseKeymap } from 'prosemirror-commands';
import { gapCursor } from 'prosemirror-gapcursor';
import { history, redo, undo } from 'prosemirror-history';
import { inputRules, wrappingInputRule } from 'prosemirror-inputrules';
import { keymap } from 'prosemirror-keymap';
import { EditorState } from 'prosemirror-state';
import { useTranslation } from 'react-i18next';
import { Stack, StackProps } from '@mantine/core';
import { EmojiMenu } from './components/Emoji/EmojiMenu';
import { getNodeViewsFromExtensions } from './utils';
import classes from '@ametista-ai/editor/styles.module.css';

export function RichEditorSimple({
  initialContent,
  onChange,
  placeholder,
  ...props
}: {
  initialContent?: string;
  onChange: (content: string) => void;
  placeholder?: string;
} & Omit<StackProps, 'onChange'>) {
  const { t } = useTranslation();
  const [emojiMenuStore] = useState(new EmojiMenuStore());

  const extensions = useMemo(
    () => [
      new Doc(),
      new Text(),
      new Paragraph(),
      new HardBreak(),
      new EmojiMenuTrigger(emojiMenuStore),
      new Placeholder(placeholder ?? t('ui.write-something')),
      // new ListItem(),
      // new UnorderedList(),
      // new OrderedList(),
    ],
    [emojiMenuStore, placeholder, t]
  );

  const nodeViews = useMemo(() => getNodeViewsFromExtensions(extensions), [extensions]);

  const [editorState, setEditorState] = useState(() => {
    const schema = createSchema(extensions);
    const plugins = getPluginsFromExtensions(extensions, schema);
    const extensionsInputRules = getInputRulesFromExtensions(extensions, schema);
    const extensionsCommands = getCommandsFromExtensions(extensions, schema);

    return EditorState.create({
      schema,
      doc: initialContent ? parseContentToDocSafe(initialContent, schema) : undefined,
      plugins: [
        ...plugins,
        keymap({
          ...baseKeymap,
          ...extensionsCommands,
          'Mod-z': undo,
          'Mod-Shift-z': redo,
          'Mod-y': redo,
        }),
        gapCursor(),
        inputRules({
          rules: [wrappingInputRule(/^\s*([-+*])\s$/, schema.nodes.list), ...extensionsInputRules],
        }),
        history(),
        reactKeys(),
      ],
    });
  });

  return (
    <EmojiMenuContext.Provider value={emojiMenuStore}>
      <ProseMirror
        state={editorState}
        nodeViews={nodeViews}
        dispatchTransaction={(tr) => {
          setEditorState((s) => s.apply(tr));

          if (tr.docChanged) {
            onChange(JSON.stringify(tr.doc.toJSON()));
          }
        }}
      >
        <Stack gap="xs" h="100%" className={classes.editor} {...props}>
          <ProseMirrorDoc data-autofocus />
        </Stack>
        <EmojiMenu />
      </ProseMirror>
    </EmojiMenuContext.Provider>
  );
}
